import { useEffect, useState } from "react";

import { getAuthToken } from "../../api/client";
import { authClient } from "../../lib/auth";
import "./loom-panel-workbench-surface.css";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

interface LoomPanelWorkbenchSurfaceProps {
  readonly embedded?: boolean;
  readonly projectId?: string;
  readonly projectName?: string;
  readonly branch?: string;
  readonly persistedProject?: boolean;
  /** Host theme; the shell reads ?theme= at boot and sets data-theme on <html>. */
  readonly theme?: "light" | "dark";
}

type WorkbenchState =
  | { stage: "loading" }
  | { stage: "ready"; url: string; email?: string }
  | { stage: "needs-thread-session" }
  | { stage: "error"; message: string };

export function buildLoomPanelWorkbenchUrl(
  token: string,
  options: LoomPanelWorkbenchSurfaceProps = {},
): string {
  const params = new URLSearchParams({ token });
  if (options.embedded) params.set("embedded", "1");
  if (options.projectId) params.set("project_id", options.projectId);
  if (options.projectName) params.set("project_name", options.projectName);
  if (options.branch) params.set("branch", options.branch);
  if (options.persistedProject !== undefined) {
    params.set("persisted_project", String(options.persistedProject));
  }
  if (options.theme) params.set("theme", options.theme);
  return `${API_BASE}/loom-workbench/panels?${params.toString()}`;
}

function currentAppPath(): string {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function LoomPanelWorkbenchSurface({
  embedded = false,
  projectId,
  projectName,
  branch,
  persistedProject,
  theme,
}: LoomPanelWorkbenchSurfaceProps) {
  const [state, setState] = useState<WorkbenchState>({ stage: "loading" });

  useEffect(() => {
    let cancelled = false;
    Promise.all([getAuthToken(), authClient.auth.getSession()])
      .then(([token, { data }]) => {
        if (cancelled) return;
        if (!token) {
          setState({ stage: "needs-thread-session" });
          return;
        }
        setState({
          stage: "ready",
          url: buildLoomPanelWorkbenchUrl(token, {
            embedded,
            projectId,
            projectName,
            branch,
            persistedProject,
            theme,
          }),
          email: data.session?.user?.email,
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          stage: "error",
          message: error instanceof Error ? error.message : String(error),
        });
      });
    return () => { cancelled = true; };
  }, [branch, embedded, persistedProject, projectId, projectName, theme]);

  const signInWithGoogle = async () => {
    const base = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
    const redirectTo = `${window.location.origin}${base}/auth/callback?next=${encodeURIComponent(currentAppPath())}`;
    const { error } = await authClient.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (error) setState({ stage: "error", message: error.message });
  };

  if (state.stage === "ready") {
    return (
      <section
        className={`loom-panel-workbench-surface${embedded ? " is-embedded" : " is-full-viewport"}`}
        data-testid="loom-panel-workbench-surface"
      >
        <iframe
          src={state.url}
          title={state.email
            ? `Loom panel workbench for ${state.email}`
            : "Loom panel workbench"}
        />
      </section>
    );
  }

  return (
    <section
      className={`loom-panel-workbench-surface is-state${embedded ? " is-embedded" : " is-full-viewport"}`}
      data-testid="loom-panel-workbench-surface"
    >
      <div className="loom-panel-workbench-state" role={state.stage === "error" ? "alert" : "status"}>
        <h1>Loom panel workbench</h1>
        {state.stage === "loading" ? <p>Loading the panel solver…</p> : null}
        {state.stage === "needs-thread-session" ? (
          <>
            <p>The panel workbench requires a Flexcompute Thread session.</p>
            <button onClick={() => void signInWithGoogle()} type="button">Continue with Google</button>
          </>
        ) : null}
        {state.stage === "error" ? <p>{state.message}</p> : null}
      </div>
    </section>
  );
}

export default LoomPanelWorkbenchSurface;
